/* iter-click-dashboard · Oct-2026 · Click-ID Dashboard.
 *
 * Shows which Reddit-tagged short links (minted via the click tracker,
 * `routes_click_tracker.py`) are driving traffic, next to total
 * Reddit-attributed signups in the same window. Reddit's own `rdt_cid`
 * is opaque per-ad-click (not per-post), so this is a channel-level
 * correlation, not a guaranteed per-click trace — the panel says so.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, MousePointerClick, Copy, DollarSign, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui/dialog';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function copyLink(code) {
  const url = `${window.location.origin}/api/r/${code}`;
  navigator.clipboard?.writeText(url);
  toast.success('Short link copied.');
}

// iter-campaign-spend — logs spend against ONE exact short link
// (tracking-only; does not post a GL entry — see backend docstring).
function LogCampaignSpendDialog({ code, onLogged }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error('Enter a spend amount greater than $0.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/marketing-roi/campaign-spend`, {
        short_link_code: code, amount_usd: amt, note: note.trim() || null,
      }, { withCredentials: true });
      toast.success(`Logged ${usd(amt)} for this link.`);
      setAmount(''); setNote(''); setOpen(false);
      onLogged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not log spend.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="h-6 gap-1 px-1.5 text-[10.5px] text-emerald-700" data-testid={`campaign-spend-open-${code}`}>
          <DollarSign className="h-3 w-3" /> Log spend
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm" data-testid={`campaign-spend-dialog-${code}`}>
        <DialogHeader>
          <DialogTitle>Log campaign spend</DialogTitle>
          <DialogDescription>
            Tracking only for this exact short link (<span className="font-mono-stat">/api/r/{code}</span>) — computes CAC
            from signups joined to this link. Doesn't post a GL entry; log the overall channel total separately in
            Marketing ROI above if you need the ledger entry.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">Amount ($)</label>
            <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00" data-testid={`campaign-spend-amount-${code}`} />
          </div>
          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">Note (optional)</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Nov 15 boost" data-testid={`campaign-spend-note-${code}`} />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={save} disabled={busy} data-testid={`campaign-spend-save-${code}`}>
            {busy ? 'Saving…' : 'Log spend'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RedditClickIdDashboard() {
  const [data, setData] = useState(null);
  const [spendByCode, setSpendByCode] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const [dashRes, spendRes] = await Promise.all([
        axios.get(`${API}/admin/marketing-roi/reddit-click-dashboard`, { withCredentials: true }),
        axios.get(`${API}/admin/marketing-roi/campaign-spend`, { withCredentials: true }),
      ]);
      setData(dashRes.data);
      const map = {};
      (spendRes.data?.by_code || []).forEach((r) => { map[r.short_link_code] = r; });
      setSpendByCode(map);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load the Click-ID dashboard.');
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const links = data?.links || [];
  const totals = data?.totals || {};

  return (
    <div className="mt-4 rounded-md border border-border bg-card" data-testid="admin-reddit-click-dashboard">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold">
          <MousePointerClick className="h-3.5 w-3.5" /> Click-ID Dashboard — Reddit posts/threads driving signups
        </span>
        <Button size="sm" variant="ghost" onClick={load} className="h-7 gap-1 text-[11px]" data-testid="reddit-click-dash-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2 border-b border-border/60 px-3 py-2.5 sm:grid-cols-4">
        <div className="rounded-sm border border-border bg-secondary/30 px-2.5 py-1.5" data-testid="reddit-click-dash-total-clicks">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total clicks ({totals.links || 0} links)</div>
          <div className="font-mono-stat text-[15px] font-semibold">{totals.clicks ?? '—'}</div>
        </div>
        <div className="rounded-sm border border-border bg-secondary/30 px-2.5 py-1.5" data-testid="reddit-click-dash-signups-30d">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Reddit signups (30d)</div>
          <div className="font-mono-stat text-[15px] font-semibold">{totals.reddit_signups_30d ?? '—'}</div>
        </div>
        <div className="rounded-sm border border-border bg-secondary/30 px-2.5 py-1.5" data-testid="reddit-click-dash-signups-all">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Reddit signups (all-time)</div>
          <div className="font-mono-stat text-[15px] font-semibold">{totals.reddit_signups_all_time ?? '—'}</div>
        </div>
        <div className="rounded-sm border border-border bg-secondary/30 px-2.5 py-1.5" data-testid="reddit-click-dash-capi">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">CAPI sent (30d)</div>
          <div className="font-mono-stat text-[15px] font-semibold">
            {data?.capi?.configured ? (data.capi.sent_30d ?? 0) : 'not configured'}
          </div>
        </div>
      </div>

      {busy && <div className="p-4 text-center text-[12px] text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></div>}
      {!busy && err && <div className="p-4 text-center text-[12px] text-destructive" data-testid="reddit-click-dash-error">{err}</div>}
      {!busy && !err && links.length === 0 && (
        <div className="p-4 text-center text-[12px] text-muted-foreground">
          No Reddit-tagged short links minted yet. Create one from the post composer (Zernio) or the Marketing panel.
        </div>
      )}
      {!busy && !err && links.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-1.5 text-left">Post / label</th>
                <th className="px-3 py-1.5 text-left">Short link</th>
                <th className="px-3 py-1.5 text-right">Clicks</th>
                <th className="px-3 py-1.5 text-right">Spend</th>
                <th className="px-3 py-1.5 text-right">Signups</th>
                <th className="px-3 py-1.5 text-right">CAC</th>
                <th className="px-3 py-1.5 text-right">Last click</th>
              </tr>
            </thead>
            <tbody>
              {links.map((l) => {
                const spend = spendByCode[l.code];
                return (
                  <tr key={l.code} className="border-t border-border/60" data-testid={`reddit-click-dash-row-${l.code}`}>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <div className="font-medium">{l.label || '(untitled link)'}</div>
                        {l.predates_api_prefix_fix && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                            data-testid={`reddit-click-dash-prefix-warning-${l.code}`}
                            title="Minted before the Oct 2 /api/r/ fix — if you already shared this link's URL externally, re-copy and re-share it, the old copy never redirected."
                          >
                            <AlertTriangle className="h-2.5 w-2.5" /> pre-fix
                          </span>
                        )}
                      </div>
                      {l.post_snippet && <div className="mt-0.5 max-w-sm truncate text-[11px] text-muted-foreground">{l.post_snippet}</div>}
                    </td>
                    <td className="px-3 py-1.5">
                      <button
                        type="button"
                        onClick={() => copyLink(l.code)}
                        className="inline-flex items-center gap-1 font-mono-stat text-[11px] text-primary hover:underline"
                        data-testid={`reddit-click-dash-copy-${l.code}`}
                        title="Copy short link"
                      >
                        /api/r/{l.code} <Copy className="h-3 w-3" />
                      </button>
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono-stat font-semibold">{l.clicks}</td>
                    <td className="px-3 py-1.5 text-right font-mono-stat">
                      {spend ? usd(spend.spend_usd) : <LogCampaignSpendDialog code={l.code} onLogged={load} />}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono-stat">{spend ? spend.signups : '—'}</td>
                    <td className="px-3 py-1.5 text-right font-mono-stat">{spend?.cac_usd != null ? usd(spend.cac_usd) : '—'}</td>
                    <td className="px-3 py-1.5 text-right text-muted-foreground">
                      {l.last_click_at ? new Date(l.last_click_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-3 py-2 text-[10.5px] text-muted-foreground">
            Reddit's own click-id (used for the Conversions API) is per-ad-click, not per-post, so clicks/signups above
            are a channel-level correlation. "Signups"/"CAC" per row (once spend is logged) ARE a precise per-link
            trace — joined via the `rc` code stamped on every redirect through this exact short link.
          </div>
        </div>
      )}
    </div>
  );
}
